// src/services/askInsights.js — client for Ask Insights (admin-only).
//
//   fetchInsightsHome(getToken)            → brief, pinned answers, freshness
//   postInsightsHome(getToken, body)       → pin / unpin / refresh
//   runInsightMeasure(getToken, run)       → one measure, no AI (pins, chips, actions)
//   askInsights(getToken, {question, history}, onEvent) → streams NDJSON events
//
// Every number the screen shows comes back from the server's measures; this
// file only moves data. See api/insights-ask.js.

async function authHeaders(getToken) {
    const token = await getToken();
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

async function asJson(res) {
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        const err = new Error(data.error || `Request failed (${res.status})`);
        err.status = res.status;
        throw err;
    }
    return data;
}

export async function fetchInsightsHome(getToken) {
    const res = await fetch('/api/insights-home', { headers: await authHeaders(getToken) });
    return asJson(res);
}

export async function postInsightsHome(getToken, body) {
    const res = await fetch('/api/insights-home', { method: 'POST', headers: await authHeaders(getToken), body: JSON.stringify(body) });
    return asJson(res);
}

export async function runInsightMeasure(getToken, run) {
    const res = await fetch('/api/insights-ask', { method: 'POST', headers: await authHeaders(getToken), body: JSON.stringify({ run }) });
    return asJson(res);
}

// Streams events to onEvent as they arrive. Falls back to reading the whole
// body when the browser/host doesn't expose a stream — same events, just later.
export async function askInsights(getToken, { question, history }, onEvent) {
    const res = await fetch('/api/insights-ask', {
        method: 'POST', headers: await authHeaders(getToken), body: JSON.stringify({ question, history }),
    });
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Request failed (${res.status})`);
    }
    const handleLine = (line) => {
        if (!line.trim()) return;
        try { onEvent(JSON.parse(line)); } catch (_) { /* partial/garbled line — ignore */ }
    };
    if (res.body && typeof res.body.getReader === 'function') {
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        for (;;) {
            // eslint-disable-next-line no-await-in-loop
            const { value, done } = await reader.read();
            if (done) break;
            buf += decoder.decode(value, { stream: true });
            let i;
            while ((i = buf.indexOf('\n')) >= 0) { handleLine(buf.slice(0, i)); buf = buf.slice(i + 1); }
        }
        handleLine(buf);
    } else {
        (await res.text()).split('\n').forEach(handleLine);
    }
}

// Plain-text CSV for a people/rows table (client-side export).
export function tableToCsv(table) {
    if (!table || !Array.isArray(table.rows)) return '';
    const cols = (table.columns || []).filter((c) => c.label);
    const withEmail = table.kind === 'people' && !cols.some((c) => c.key === 'email');
    const head = [...cols.map((c) => c.label), ...(withEmail ? ['Email'] : [])];
    const esc = (v) => {
        let s = v == null ? '' : String(v);
        // Spreadsheet formula injection: a cell starting with = + - @ (e.g. a
        // member who named herself "=HYPERLINK(...)") is neutralised.
        if (/^[=+\-@\t\r]/.test(s) && !/^-?\d[\d,.]*$/.test(s)) s = `'${s}`;
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [head.map(esc).join(',')];
    table.rows.forEach((r) => lines.push([...cols.map((c) => r[c.key]), ...(withEmail ? [r.email] : [])].map(esc).join(',')));
    return lines.join('\n');
}
